// A thin OpenRouter client over fetch: chat completions with optional JSON-schema output and
// Anthropic-style cache breakpoints. The pipeline talks to a `Model` function so tests can
// substitute a stub; this file is the only one that knows the wire format.

export interface ContentPart {
  type: "text";
  text: string;
  /** Anthropic prompt caching through OpenRouter: mark the end of a stable prefix. */
  cache_control?: { type: "ephemeral" };
}

export interface ChatMessage {
  role: "system" | "user" | "assistant";
  content: string | ContentPart[];
}

export interface ChatRequest {
  model: string;
  messages: ChatMessage[];
  /** Output budget; the model's thinking counts against it. */
  maxTokens: number;
  /** A JSON schema the reply must satisfy (OpenRouter structured outputs). */
  schema?: { name: string; strict: boolean; schema: unknown };
  temperature?: number;
  /** How much the model thinks first; overrides the client's default for this call. */
  reasoning?: ReasoningEffort;
}

export type ReasoningEffort = "low" | "medium" | "high";

export interface Usage {
  prompt: number;
  completion: number;
  /** Prompt tokens served from cache, when the provider reports them. */
  cached: number;
  /** Prompt tokens written to the cache by this call. */
  cacheWrite: number;
  /** Completion tokens spent thinking (billed as output), when the provider reports them. */
  reasoning: number;
  /** Dollars, when OpenRouter reports the cost. */
  cost: number;
}

export interface ChatResponse {
  text: string;
  usage: Usage;
  model: string;
  /** Which upstream served it; caches live per provider. */
  provider: string;
}

export type Model = (req: ChatRequest) => Promise<ChatResponse>;

/**
 * The reply ran past `maxTokens`. Carries what was spent and how the reply ended, so the log
 * can say whether the thinking or the file was the length; `code` survives a trip through the
 * proxy as JSON.
 */
export class LengthError extends Error {
  readonly code = "length";
  constructor(
    message: string,
    readonly usage: Usage,
    readonly tail: string,
  ) {
    super(message);
    this.name = "LengthError";
  }
}

export interface OpenRouterOptions {
  apiKey: string;
  baseUrl?: string;
  /** Shown on OpenRouter's dashboard; optional. */
  appName?: string;
  /**
   * Providers to try first, in order. Prompt caches live per provider, so a prefix cached at
   * Anthropic is a miss at Bedrock; preferring one keeps the cache warm. Default ["Anthropic"].
   */
  providers?: string[];
  /**
   * How much the model thinks before answering (OpenRouter's `reasoning.effort`). Unset leaves
   * the provider's default, which for Claude 5 is adaptive thinking; it cannot be disabled on
   * these endpoints ("Reasoning is mandatory", 2026-10-16), but "low" measured zero thinking
   * tokens on a small task. Thinking is billed as output.
   */
  reasoning?: ReasoningEffort;
  fetch?: typeof fetch;
}

/** The real thing. Needs OPENROUTER_API_KEY. */
export function openRouter(o: OpenRouterOptions): Model {
  const base = (o.baseUrl ?? "https://openrouter.ai/api/v1").replace(/\/$/, "");
  const f = o.fetch ?? fetch;
  return async (req) => {
    const body: Record<string, unknown> = {
      model: req.model,
      messages: req.messages,
      max_tokens: req.maxTokens,
      usage: { include: true },
    };
    // Current Claude models reject sampling settings, and with require_parameters a temperature
    // empties the routing entirely; send one only when a caller asks for it.
    if (req.temperature !== undefined) body.temperature = req.temperature;
    const effort = req.reasoning ?? o.reasoning;
    if (effort) body.reasoning = { effort };
    const provider: Record<string, unknown> = { order: o.providers ?? ["Anthropic"], allow_fallbacks: true };
    if (req.schema) {
      body.response_format = { type: "json_schema", json_schema: req.schema };
      // Only providers that honour the schema may serve the request.
      provider.require_parameters = true;
    }
    body.provider = provider;
    const res = await f(`${base}/chat/completions`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${o.apiKey}`,
        "Content-Type": "application/json",
        "X-Title": o.appName ?? "prompt-to-multiplay",
      },
      body: JSON.stringify(body),
    });
    const text = await res.text();
    if (!res.ok) throw new Error(`OpenRouter ${res.status}: ${text.slice(0, 400)}`);
    const data = JSON.parse(text) as {
      model?: string;
      provider?: string;
      choices?: { message?: { content?: string | { type: string; text?: string }[] }; finish_reason?: string }[];
      usage?: {
        prompt_tokens?: number;
        completion_tokens?: number;
        cost?: number;
        prompt_tokens_details?: { cached_tokens?: number; cache_write_tokens?: number };
        completion_tokens_details?: { reasoning_tokens?: number };
      };
      error?: { message?: string };
    };
    if (data.error) throw new Error(`OpenRouter: ${data.error.message ?? JSON.stringify(data.error)}`);
    const choice = data.choices?.[0];
    const content = choice?.message?.content;
    const out = typeof content === "string" ? content : (content ?? []).map((p) => p.text ?? "").join("");
    const usage: Usage = {
      prompt: data.usage?.prompt_tokens ?? 0,
      completion: data.usage?.completion_tokens ?? 0,
      cached: data.usage?.prompt_tokens_details?.cached_tokens ?? 0,
      cacheWrite: data.usage?.prompt_tokens_details?.cache_write_tokens ?? 0,
      reasoning: data.usage?.completion_tokens_details?.reasoning_tokens ?? 0,
      cost: data.usage?.cost ?? 0,
    };
    if (choice?.finish_reason === "length") {
      const tail = out.slice(-200).replace(/\s+/g, " ").trim();
      throw new LengthError(
        `OpenRouter: the reply hit max_tokens (${req.maxTokens}): ${usage.completion} completion tokens, ${usage.reasoning} of them thinking${tail ? `; it ends "…${tail}"` : "; no text came back"}`,
        usage,
        tail,
      );
    }
    return { text: out, model: data.model ?? req.model, provider: data.provider ?? "", usage };
  };
}

/** Strip a ```js fence if the model wrapped its code in one. */
export function unfence(text: string): string {
  const m = /^\s*```[a-zA-Z]*\s*\n([\s\S]*?)\n```\s*$/.exec(text);
  return (m ? m[1] : text).trim() + "\n";
}

/** Parse JSON out of a reply, tolerating a fence around it. */
export function parseJson<T>(text: string): T {
  const body = unfence(text).trim();
  const start = body.indexOf("{");
  const end = body.lastIndexOf("}");
  return JSON.parse(start >= 0 && end > start ? body.slice(start, end + 1) : body) as T;
}
